import { Mail } from 'lucide-react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { PaginatedTable } from '@/modules/common';
import { useEventOrdersHistory } from '@/modules/events/api';
import { FiltersChangeType } from '@/modules/events/hooks';
import { EventOrder } from '@/modules/events/types';

import { getOrdersHistoryColumns } from './columns';
import {
  OrdersFilters,
  OrdersHistoryEventActions,
  SendSmsDialog,
} from './components';

export const OrdersHistoryTab = () => {
  const params = useParams<{ id: string }>();
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [sendOneSmsOrder, setSendOneSmsOrder] = useState(null);
  const [isSendSmsDialogOpen, setIsSendSmsDialogOpen] = useState(false);

  const { getValues } = useFormContext();

  const eventId = params.id;
  const eventName = getValues('name');

  const {
    isFetching: isFetchingOrders,
    data: ordersData,
    isGeneratingReport,
    generateReport,
    filters,
    setFilters: filtersChange,
    ...tableProps
  } = useEventOrdersHistory(eventId, eventName, selectedOrders);

  const onSendSms = (id: string) => {
    setSendOneSmsOrder(id);
    setIsSendSmsDialogOpen(true);
  };

  const columns = getOrdersHistoryColumns(eventId, onSendSms);

  return (
    <div className="flex flex-col gap-4">
      <p className="font-semibold text-lg">Orders Table</p>

      <div className="flex flex-col gap-4">
        <PaginatedTable<EventOrder>
          pinEnabled
          data={ordersData}
          columns={columns}
          searchable
          searchPlaceholder="Search by name or phone"
          emptyTitle="No orders yet"
          emptyDescription=""
          sizeStyleProperty="minWidth"
          isLoading={isFetchingOrders}
          onRowSelectionChange={setSelectedOrders}
          tableCellClassName="py-1"
          initialState={{
            columnPinning: {
              right: ['actions'],
              left: ['select'],
            },
          }}
          totalCountLabel="orders"
          {...tableProps}
        >
          <OrdersFilters
            filters={filters}
            onFilters={filtersChange as FiltersChangeType}
          />

          {ordersData.length > 0 && (
            <OrdersHistoryEventActions
              onGenerateReport={generateReport}
              isGeneratingReport={isGeneratingReport}
            />
          )}

          {Object.keys(selectedOrders).length > 0 && (
            <Button
              size="sm"
              onClick={() => setIsSendSmsDialogOpen(true)}
              startIcon={<Mail />}
            >
              Send SMS
            </Button>
          )}
        </PaginatedTable>
      </div>

      <SendSmsDialog
        eventId={eventId}
        isOpen={isSendSmsDialogOpen}
        onOpenChange={() => {
          setIsSendSmsDialogOpen(false);
          if (sendOneSmsOrder) {
            setSendOneSmsOrder(null);
          }
        }}
        orders={ordersData}
        orderIds={sendOneSmsOrder ? [sendOneSmsOrder] : selectedOrders}
      />
    </div>
  );
};
